<template>
  <div class="input-container" :class="{error : error, disabled: disabled }">
    <label :for="id">
      <slot />
      <transition name="showErrorIcon">
        <icon v-if="error" size="small" icon="error" />
      </transition>
    </label>
    <input
      v-if="!canMask"
      :type="type"
      :id="id"
      :value="value"
      :min="min"
      :max="max"
      :minLength="minLength"
      :maxLength="maxLength"
      :name="name"
      :autocomplete="autocomplete"
      @input="onChange"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      @keydown="handleKeyDown"
    />

    <masked-input
      v-else
      :type="type"
      :id="id"
      :value="value"
      :min="min"
      :max="max"
      :minLength="minLength"
      :maxLength="maxLength"
      @input="onChange"
      :placeholder="placeholder"
      :mask="mask.pattern"
      :guide="mask.guide"
      :pipe="mask.pipe"
      :keepCharPositions="mask.keepCharPositions"
      :name="name"
      :autocomplete="autocomplete"
      :disabled="disabled"
      :readonly="readonly"
      @keydown="handleKeyDown"
      />

    <p class="form-control-feedback">
      <slot name="feedback" />
    </p>
    <p class="form-control-info">
      <slot name="info" />
    </p>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid'
import MaskedInput from 'vue-text-mask'

export default {
  components: {
    MaskedInput
  },
  props: {
    type: {
      type: String,
      required: false,
      default: 'text',
      validator: value => ['text', 'password', 'email', 'tel', 'number', 'date', 'button'].indexOf(value) !== -1
    },
    value: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      default: '',
      required: false
    },
    min: {
      type: String,
      required: false
    },
    max: {
      type: String,
      required: false
    },
    minLength: {
      type: Number,
      required: false
    },
    maxLength: {
      type: Number,
      required: false
    },
    meta: {
      type: Object,
      required: false,
      default: () => {}
    },
    name: {
      type: String,
      required: false
    },
    autocomplete: {
      type: String,
      required: false
    },
    error: {
      type: Boolean,
      required: false
    },
    disabled: {
      type: Boolean,
      required: false
    },
    readonly: {
      type: Boolean,
      required: false
    }
  },
  methods: {
    onChange (e) {
      this.$emit('input', (e.target) ? e.target.value : e)
    },
    handleKeyDown (e) {
      const keyCodes = [69, 187, 189, 190] // e, =, -, +
      const isNum = /^[0-9]*$/.test(e.key)
      const value = (e.target) ? e.target.value : e

      if (this.type === 'number') {
        const len = value.toString().length
        if (keyCodes.includes(e.keyCode) || (len >= this.maxLength && isNum)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }
    }
  },
  data () {
    return {
      id: null
    }
  },
  computed: {
    canMask () {
      return (['email', 'number', 'date', 'button'].indexOf(this.type) === -1)
    },
    mask () {
      return Object.assign({}, {
        pattern: false,
        guide: false,
        pipe: null,
        keepCharPositions: false
      }, ((this.meta && this.meta.mask) ? this.meta.mask : {}))
    }
  },
  mounted () {
    this.id = `text-input-${uuid()}`
  }
}
</script>

<style lang="scss" scoped>


.input-container {
  display: flex;
  flex-direction: column;
}

.form-control-feedback,
.form-control-info {
  margin: 0;
}

.icon {
  float: right;
  margin-bottom: -200px;
  transform: translate3d(0, -25%, 0);
}

.showErrorIcon-enter,
.showErrorIcon-leave {
  transform: scale(0);
  opacity: 0;
}

.input-container {
  margin-bottom: $d-xx-small;
}

.input-container .disabled {
  color: $deprecated-grey-medium-dark;
}

label {
  @include font('040');
  display: block;
  text-transform: none;
  color: $color-black;
  margin-bottom: $d-xx-small;
  display: block;
}

input {
  @include transition-fast;
  @include font('050');

  outline: 0;
  border: 0;
  width: 100%;
  padding: $d-small;
  margin-bottom: $d-tiny;
  background-color: rgba($color-white, 0);
  border-bottom: $line-medium;
  border-radius: 2px 2px 0 0;
  outline-offset: 4px;

  &[type="button"] {
    text-align: left;
  }

  &[type="button"] {
    text-align: left;
  }

  &:active {
    border-bottom: $line-dark;
    padding-top: $d-small;
  }

  &:not(:read-only):focus {
    border-radius: 2px 2px 0 0;
    padding-top: $d-small;
    box-shadow:
      inset 0 -3px 0 $color-cream,
      inset 0 -100px 0 $color-cream-light;
    color: $color-gray;

    &::placeholder {
      color: $color-gray;
    }
  }

  &:disabled {
    color: $deprecated-grey-medium;
  }
}

.form-control-feedback, .form-control-info {
  @include body-sml;
  padding-top: 0;
}

.form-control-feedback {
  min-height: 1.6em;

  .error {
    color: $deprecated-color-ui-error;
  }
}
</style>
